import React, { useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';

const TrendChart = ({ data, logoSrc, onClose }) => {
  // Memoize chart data transformation - focus on closing prices
  const chartData = useMemo(() => {
    if (!data?.dataPoints) return [];
    return data.dataPoints.map(point => ({
      x: new Date(point.time).getTime(),
      y: point.close, // Focus on closing prices
      originalData: point
    }));
  }, [data?.dataPoints]);

  let trendColor = '#1f2937'; // default color
    if (data?.xAxisAnnotations && data.xAxisAnnotations.length > 0) {
      trendColor = data.xAxisAnnotations[0].style.lineColor;
    }

  // Memoize series data creation
  const seriesData = useMemo(() => {
    // Helper function to identify trend calculation points based on timeframe
    const getTrendCalculationPoints = (dataPoints, timeframe) => {
      if (!dataPoints || dataPoints.length === 0) return [];
      
      if (timeframe === 'DAY') {
        // For daily: all points are trend calculation points
        return dataPoints.map(point => new Date(point.time).getTime());
      }
      
      if (timeframe === 'WEEK') {
        // For weekly: last traded day of each week
        const weekGroups = {};
        
        dataPoints.forEach(point => {
          const date = new Date(point.time);
          // Get the Monday of the week (start of week)
          const monday = new Date(date);
          monday.setDate(date.getDate() - date.getDay() + 1);
          const weekKey = monday.toISOString().split('T')[0];
          
          if (!weekGroups[weekKey] || new Date(point.time) > new Date(weekGroups[weekKey].time)) {
            weekGroups[weekKey] = point;
          }
        });
        
        return Object.values(weekGroups).map(point => new Date(point.time).getTime());
      }
      
      if (timeframe === 'MONTH') {
        // For monthly: last traded day of each month
        const monthGroups = {};
        
        dataPoints.forEach(point => {
          const date = new Date(point.time);
          const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
          
          if (!monthGroups[monthKey] || new Date(point.time) > new Date(monthGroups[monthKey].time)) {
            monthGroups[monthKey] = point;
          }
        });
        
        return Object.values(monthGroups).map(point => new Date(point.time).getTime());
      }
      
      return [];
    };

    // Get timeframe from annotations or default to DAY
    const timeframe = data?.xAxisAnnotations?.[0]?.metadata?.timeframe || 'DAY';
    const trendCalculationTimes = getTrendCalculationPoints(data?.dataPoints, timeframe);
    
    // Only show trend calculation points (last traded price for each period)
    const trendPointsData = chartData.filter(point => 
      trendCalculationTimes.includes(point.x)
    );

    

    const trendCalculationSeries = {
      name: `${timeframe === 'DAY' ? 'Daily' : timeframe === 'WEEK' ? 'Weekly' : 'Monthly'} Price Points`,
      data: trendPointsData,
      color: trendColor
    };

    return [trendCalculationSeries];
  }, [chartData, data?.xAxisAnnotations, data?.dataPoints]);

  // Enhanced annotations processing
  const annotations = (() => {
    const xAxisAnnotations = data?.xAxisAnnotations ? data.xAxisAnnotations.map(annotation => ({
      x: new Date(annotation.startTime).getTime(),
      x2: new Date(annotation.endTime).getTime(),
      strokeDashArray: 0,
      borderColor: 'transparent',
      fillColor: 'transparent',
      opacity: 0.08,
      label: {
        text: `${annotation.metadata.category} Trend (${annotation.metadata.percentageChange}%)`,
        position: 'top',
        offsetY: 10,
        style: {
          color: annotation.style.lineColor,
          background: 'rgba(255, 255, 255, 0.9)',
          fontSize: '12px',
          fontWeight: 'bold',
          borderRadius: '4px',
          padding: {
            left: 8,
            right: 8,
            top: 4,
            bottom: 4
          }
        }
      }
    })) : [];

    // Add light background fill from 7th/8th last point
    const backgroundFillAnnotation = (() => {
      // Get timeframe and trend calculation points
      const timeframe = data?.xAxisAnnotations?.[0]?.metadata?.timeframe || 'DAY';
      const getTrendCalculationPoints = (dataPoints, timeframe) => {
        if (!dataPoints || dataPoints.length === 0) return [];
        
        if (timeframe === 'DAY') {
          return dataPoints.map(point => new Date(point.time).getTime());
        }
        
        if (timeframe === 'WEEK') {
          const weekGroups = {};
          dataPoints.forEach(point => {
            const date = new Date(point.time);
            const monday = new Date(date);
            monday.setDate(date.getDate() - date.getDay() + 1);
            const weekKey = monday.toISOString().split('T')[0];
            
            if (!weekGroups[weekKey] || new Date(point.time) > new Date(weekGroups[weekKey].time)) {
              weekGroups[weekKey] = point;
            }
          });
          return Object.values(weekGroups).map(point => new Date(point.time).getTime());
        }
        
        if (timeframe === 'MONTH') {
          const monthGroups = {};
          dataPoints.forEach(point => {
            const date = new Date(point.time);
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            
            if (!monthGroups[monthKey] || new Date(point.time) > new Date(monthGroups[monthKey].time)) {
              monthGroups[monthKey] = point;
            }
          });
          return Object.values(monthGroups).map(point => new Date(point.time).getTime());
        }
        
        return [];
      };

      const trendCalculationTimes = getTrendCalculationPoints(data?.dataPoints, timeframe);
      const trendPointsData = chartData.filter(point => 
        trendCalculationTimes.includes(point.x)
      );

      if (trendPointsData.length >= 8) {
        // Check if LTP differs from last period's traded price
        const lastPoint = trendPointsData[trendPointsData.length - 1];
        const ltp = data?.stock?.lastTradedPrice;
        const ltpDiffersFromLastPeriod = ltp && Math.abs(ltp - lastPoint.y) > 0.01; // Allow small floating point differences
        
        // Start from 7th last (index -7) or 8th last (index -8) based on LTP difference
        const startIndex = ltpDiffersFromLastPeriod ? -8 : -7;
        const startPoint = trendPointsData[trendPointsData.length + startIndex];
        const endPoint = trendPointsData[trendPointsData.length - 1];
        
        if (startPoint && endPoint) {
          return {
            x: startPoint.x,
            x2: endPoint.x,
            strokeDashArray: 0,
            borderColor: 'transparent',
            fillColor: trendColor, // Slightly darker blue background
            opacity: 0.05 // Increased opacity to make it more visible
          };
        }
      }
      
      return null;
    })();

    // Combine all xAxis annotations
    const allXAxisAnnotations = backgroundFillAnnotation 
      ? [...xAxisAnnotations, backgroundFillAnnotation]
      : xAxisAnnotations;

    const yAxisAnnotations = data?.yAxisAnnotations ? data.yAxisAnnotations.map(annotation => ({
      y: annotation.priceLevel,
      strokeDashArray: 8,
      borderColor: annotation.style.lineColor,
      borderWidth: 0.5, // Made even thinner
      label: {
        text: `${annotation.metadata.levelType}: ₹${annotation.priceLevel}`,
        position: 'right',
        offsetX: 120, // Further increased offset to ensure labels are well outside chart area
        style: {
          color: '#ffffff',
          background: annotation.style.lineColor,
          fontSize: '11px',
          fontWeight: 'bold',
          borderRadius: '4px',
          padding: {
            left: 8,
            right: 8,
            top: 4,
            bottom: 4
          }
        }
      }
    })) : [];

    return {
      xaxis: allXAxisAnnotations,
      yaxis: yAxisAnnotations
    };
  })();

  // Chart options
  const options = (() => {
    // Calculate y-axis range to include support/resistance levels
    const dataValues = chartData.map(point => point.y);
    const minDataValue = Math.min(...dataValues);
    const maxDataValue = Math.max(...dataValues);
    
    // Get support and resistance levels
    const supportLevel = data?.yAxisAnnotations?.find(annotation => 
      annotation.metadata.levelType === 'SUPPORT'
    )?.priceLevel;
    const resistanceLevel = data?.yAxisAnnotations?.find(annotation => 
      annotation.metadata.levelType === 'RESISTANCE'
    )?.priceLevel;
    
    // Calculate the range including support/resistance levels
    const allValues = [minDataValue, maxDataValue];
    if (supportLevel) allValues.push(supportLevel);
    if (resistanceLevel) allValues.push(resistanceLevel);
    
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    
    // Add 8% padding on both sides for better visibility
    const padding = (maxValue - minValue) * 0.08;
    const yAxisMin = minValue - padding;
    const yAxisMax = maxValue + padding;

    // Compute highlighted indices (last 7 or 8 points inside the background fill range)
    const timeframe = data?.xAxisAnnotations?.[0]?.metadata?.timeframe || 'DAY';
    const getTrendCalculationPoints = (dataPoints, timeframe) => {
      if (!dataPoints || dataPoints.length === 0) return [];
      if (timeframe === 'DAY') {
        return dataPoints.map(point => new Date(point.time).getTime());
      }
      if (timeframe === 'WEEK') {
        const weekGroups = {};
        dataPoints.forEach(point => {
          const date = new Date(point.time);
          const monday = new Date(date);
          monday.setDate(date.getDate() - date.getDay() + 1);
          const weekKey = monday.toISOString().split('T')[0];
          if (!weekGroups[weekKey] || new Date(point.time) > new Date(weekGroups[weekKey].time)) {
            weekGroups[weekKey] = point;
          }
        });
        return Object.values(weekGroups).map(point => new Date(point.time).getTime());
      }
      if (timeframe === 'MONTH') {
        const monthGroups = {};
        dataPoints.forEach(point => {
          const date = new Date(point.time);
          const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
          if (!monthGroups[monthKey] || new Date(point.time) > new Date(monthGroups[monthKey].time)) {
            monthGroups[monthKey] = point;
          }
        });
        return Object.values(monthGroups).map(point => new Date(point.time).getTime());
      }
      return [];
    };

    const trendCalculationTimes = getTrendCalculationPoints(data?.dataPoints, timeframe);
    const trendPointsData = chartData.filter(point => trendCalculationTimes.includes(point.x));
    let highlightedIndices = [];
    if (trendPointsData.length >= 7) {
      const lastPoint = trendPointsData[trendPointsData.length - 1];
      const ltp = data?.stock?.lastTradedPrice;
      const ltpDiffersFromLastPeriod = ltp && Math.abs(ltp - lastPoint.y) > 0.01;
      const highlightCount = Math.min(trendPointsData.length, ltpDiffersFromLastPeriod ? 8 : 7);
      const startIndex = Math.max(0, trendPointsData.length - highlightCount);
      highlightedIndices = Array.from({ length: trendPointsData.length - startIndex }, (_, i) => startIndex + i);
    }

    const discreteMarkers = highlightedIndices.map(idx => ({
      seriesIndex: 0,
      dataPointIndex: idx,
      fillColor: trendColor,
      strokeColor: '#ffffff',
      size: 4
    }));

    return {
      chart: {
        type: 'line',
        height: 450,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: true,
          type: 'x'
        },
        background: '#fafafa'
      },
      title: {
        text: `${data?.stock?.stockLabel} - Closing Price Trend`,
        align: 'left',
        style: {
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#1f2937'
        }
      },
      subtitle: {
        text: data?.stock?.name || '',
        align: 'left',
        style: {
          fontSize: '14px',
          color: '#6b7280'
        }
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'dd MMM',
          style: {
            fontSize: '12px',
            colors: '#6b7280'
          }
        },
        axisBorder: {
          show: true,
          color: '#e5e7eb'
        },
        axisTicks: {
          show: true,
          color: '#e5e7eb'
        }
      },
      yaxis: {
        min: yAxisMin,
        max: yAxisMax,
        forceNiceScale: true,
        tooltip: {
          enabled: true
        },
        labels: {
          formatter: (val) => `₹${val.toFixed(1)}`,
          style: {
            fontSize: '12px',
            colors: '#6b7280'
          }
        },
        axisBorder: {
          show: true,
          color: '#e5e7eb'
        }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: 'dd MMM yyyy'
        },
        y: {
          formatter: (val) => `₹${val.toFixed(2)}`
        },
        style: {
          fontSize: '12px'
        }
      },
      stroke: {
        curve: 'smooth',
        width: 2,
        colors: [seriesData[0]?.color || '#1f2937']
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'light',
          type: 'vertical',
          shadeIntensity: 0.1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100]
        }
      },
      markers: {
        size: 2,
        colors: ['#000000'],
        strokeColors: '#ffffff',
        strokeWidth: 2,
        discrete: discreteMarkers,
        hover: {
          size: 5
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        fontWeight: 500,
        markers: {
          width: 12,
          height: 12,
          radius: 6
        }
      },
      grid: {
        show: true,
        borderColor: '#e5e7eb',
        strokeDashArray: 2,
        padding: {
          top: 20,
          right: 140, // Further increased right padding to match the label positioning
          bottom: 20,
          left: 20
        }
      },
      annotations: annotations
    };
  })();

  // Memoize series data for ApexCharts
  const series = useMemo(() => {
    return seriesData.map(seriesItem => ({
      name: seriesItem.name,
      data: seriesItem.data.map(point => ({
        x: point.x,
        y: point.y
      }))
    }));
  }, [seriesData]);

  // Use useCallback for event handlers
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Early return for no data case
  if (!data || !data.dataPoints) {
    return (
      <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
           style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
        <div className="bg-white p-4 rounded shadow-lg text-center">
          <div className="text-warning mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h5 className="text-warning mb-3">No Data Available</h5>
          <p className="mb-3">
            {!data ? 'No data received from API' : 'Missing dataPoints in API response'}
          </p>
          <button 
            className="btn btn-secondary" 
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" 
         style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
      <div className="bg-white p-4 rounded shadow-lg" style={{ width: '95%', maxWidth: '900px' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <img 
                src={logoSrc} 
                alt={`${data.stock?.stockLabel} logo`}
                style={{ width: '40px', height: '40px', objectFit: 'contain' }}
              />
            </div>
            <span className="badge bg-success px-3 py-2" style={{ fontSize: '14px', fontWeight: '600' }}>
              {data.stock?.stockLabel}
            </span>
            <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '20px', lineHeight: '1.2' }}>
              {data.stock?.name}
            </h4>
          </div>
          <button 
            className="btn btn-outline-secondary btn-sm" 
            onClick={handleClose}
            style={{ fontSize: '18px', lineHeight: '1', padding: '0.25rem 0.5rem' }}
          >
            ×
          </button>
        </div>
        
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
              <div className="text-muted small fw-medium">Last Traded Price</div>
              <div className="fw-bold text-success" style={{ fontSize: '16px' }}>
                ₹{data.stock?.lastTradedPrice?.toLocaleString('en-IN')}
              </div>
            </div>
          </div>
          
          {data.xAxisAnnotations && data.xAxisAnnotations.length > 0 && (
            <>
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
                  <div>
                    <div className="text-muted small fw-medium">Trend Direction</div>
                    <div className="text-muted small">
                      {data.xAxisAnnotations[0].metadata.timeframe}
                    </div>
                  </div>
                  <div className={`fw-bold ${data.xAxisAnnotations[0].metadata.category === 'UP' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '16px' }}>
                    {data.xAxisAnnotations[0].metadata.category}
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border" style={{ height: '60px' }}>
                  <div>
                    <div className="text-muted small fw-medium">Price Change</div>
                    <div className="text-muted small">
                      Trend Period
                    </div>
                  </div>
                  <div className={`fw-bold ${data.xAxisAnnotations[0].metadata.percentageChange >= 0 ? 'text-success' : 'text-danger'}`} style={{ fontSize: '16px' }}>
                    {data.xAxisAnnotations[0].metadata.percentageChange}%
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        <Chart
          key={`trend-chart-${Date.now()}`}
          options={options}
          series={series}
          type="line"
          height={450}
        />
      </div>
    </div>
  );
};

export default TrendChart; 